import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import PageLayout, {
  PageSection,
} from "../../components/navigation/PageLayout";
import EmployeeListTable from "./components/EmployeeListTable";
import EmployeeDetailPanel from "./components/EmployeeDetailPanel";
import EmployeeFilters from "./components/EmployeeFilters";
import BulkActionsBar from "./components/BulkActionsBar";
import AddEmployeeModal from "./components/AddEmployeeModal";
import CsvImportModal from "./components/CsvImportModal";
import Pagination from "../../components/ui/Pagination";
import { useEmployees, useEmployeeMutations } from "../../hooks/useEmployees";
import { useJobRoles } from "../../hooks/useJobRoles";
import { useLocationContext } from "../../contexts/LocationContext";
import { useTenantContext } from "../../contexts/TenantContext";
import Icon from "../../components/AppIcon";
import LocationSelectorModal from "../../components/LocationSelectorModal";
import { applyAllFilters } from "../../utils/employeeFilters";

// Standard page size for all list views
const DEFAULT_PAGE_SIZE = 25;

const Employees = () => {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedEmploymentType, setSelectedEmploymentType] = useState("");
  const [selectedEligibilityStatus, setSelectedEligibilityStatus] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [notification, setNotification] = useState(null);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Tenant context for global admin check
  const { selectedTenant, needsTenantSelection, isGlobalAdmin } =
    useTenantContext();

  // Only fetch when tenant is selected (or for non-global admins)
  const shouldFetchData = !isGlobalAdmin || selectedTenant;

  // Fetch employees from API
  const {
    employees: apiEmployees,
    loading,
    error,
    refetch,
  } = useEmployees({
    autoFetch: shouldFetchData,
  });
  const {
    createEmployee,
    syncFromProvider,
    updateEmployee,
    updateEligibility,
    bulkUpdateEligibility,
    loading: mutationLoading,
  } = useEmployeeMutations();

  // Fetch job roles for the add employee form
  const { jobRoles } = useJobRoles({ autoFetch: shouldFetchData });

  // Get locations from context for the add employee form
  const { locations, needsLocationSelection } = useLocationContext();

  // Transform API data to match component expectations
  const transformedEmployees = useMemo(() => (apiEmployees || []).map((emp) => ({
    id: emp.id,
    name: emp.fullName || `${emp.firstName || ""} ${emp.lastName || ""}`.trim(),
    firstName: emp.firstName,
    lastName: emp.lastName,
    photo:
 emp.photoUrl ||
      `https://ui-avatars.com/api/?name=${encodeURIComponent(emp.firstName || "")}+${encodeURIComponent(emp.lastName || "")}&background=1a365d&color=fff`,
    role: emp.jobTitle || emp.primaryJobRoleName || "Unassigned",
    employmentType:
      emp.contractedHoursPerWeek >= 35
        ? "Full-time"
        : emp.contractedHoursPerWeek >= 20
       ? "Part-time"
      : "Casual",
    eligibilityStatus: emp.isEligibleForTronc ? "Eligible" : "Not Eligible",
    serviceHours: emp.contractedHoursPerWeek || 0,
    startDate: emp.startDate,
    email: emp.email,
phone: emp.phoneNumber,
    department: emp.department,
    location: emp.location,
    isActive: emp.isActive,
    isEligibleForTronc: emp.isEligibleForTronc,
  })), [apiEmployees]);

  const breadcrumbs = [
    { label: "Dashboard", path: "/dashboard-overview" },
    { label: "Employees", path: "/employees" },
  ];

  // Stats for header
  const stats = {
    total: transformedEmployees.length,
    eligible: transformedEmployees.filter(
      (e) => e.eligibilityStatus === "Eligible",
    ).length,
    notEligible: transformedEmployees.filter(
      (e) => e.eligibilityStatus === "Not Eligible",
    ).length,
  };

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleSyncEmployees = async () => {
    try {
      await syncFromProvider("Sona");
      refetch();
      showNotification("Employees synced successfully");
    } catch (err) {
      showNotification(err.message || "Sync failed", "error");
 }
  };

  const handleEmployeeClick = (employee) => {
    setSelectedEmployee(employee);
    setIsDetailPanelOpen(true);
  };

  const handleCloseDetailPanel = () => {
    setIsDetailPanelOpen(false);
    setTimeout(() => setSelectedEmployee(null), 300);
  };

  const handleAddEmployee = async (employeeData) => {
    try {
      await createEmployee(employeeData);
   setIsAddModalOpen(false);
      refetch();
      showNotification("Employee added successfully");
    } catch (err) {
      showNotification(err.message || "Failed to add employee", "error");
      throw err;
    }
  };

  const handleImportEmployees = async (employees) => {
    try {
  let successCount = 0;
    let errorCount = 0;

      for (const emp of employees) {
        try {
          await createEmployee(emp);
          successCount++;
     } catch (err) {
  errorCount++;
          console.error("Failed to import employee:", emp.email, err);
 }
      }

      refetch();

      if (errorCount === 0) {
     showNotification(`Successfully imported ${successCount} employees`);
  } else {
   showNotification(
          `Imported ${successCount} employees, ${errorCount} failed`,
      "warning",
        );
      }
    } catch (err) {
      showNotification(err.message || "Import failed", "error");
      throw err;
    }
  };

  // Filter employees using the utility function
  const filteredEmployees = useMemo(() => applyAllFilters(
    transformedEmployees,
 apiEmployees,
    {
      searchTerm,
      selectedRole,
      selectedDepartment,
      selectedEmploymentType,
      selectedEligibilityStatus,
    }
), [transformedEmployees, apiEmployees, searchTerm, selectedRole, selectedDepartment, selectedEmploymentType, selectedEligibilityStatus]);

  // Paginated employees
  const paginatedEmployees = useMemo(() => {
    const startIndex = (pagination.page - 1) * pagination.pageSize;
    return filteredEmployees.slice(startIndex, startIndex + pagination.pageSize);
  }, [filteredEmployees, pagination.page, pagination.pageSize]);

  const totalPages = Math.ceil(filteredEmployees.length / pagination.pageSize);

  // Reset to page 1 when filters change
  const handleFilterChange = (setter) => (value) => {
    setter(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination({ page: 1, pageSize: newPageSize });
  };

  // Export helpers - comprehensive CSV with proper formatting
  const exportToCsv = (employees, filename) => {
    const headers = [
      "First Name",
      "Last Name",
      "Email",
      "Phone Number",
      "Job Title",
      "Department",
      "Location",
      "Contracted Hours/Week",
  "Employment Type",
      "TRONC Eligible",
      "Start Date",
      "Status",
    ];
    
  const rows = employees.map((e) => [
      e.firstName || "",
      e.lastName || "",
      e.email || "",
      e.phone || "",
      e.role || "",
      e.department || "",
      e.location || "",
      e.serviceHours || 0,
      e.employmentType || "",
      e.isEligibleForTronc ? "Yes" : "No",
      e.startDate ? new Date(e.startDate).toLocaleDateString("en-GB") : "",
 e.isActive !== false ? "Active" : "Inactive",
    ]);

    // Properly escape CSV values - handle quotes, commas, newlines
 const escapeCell = (cell) => {
      const str = (cell ?? "").toString();
      // If contains comma, quote, or newline, wrap in quotes and escape internal quotes
      if (str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("\r")) {
 return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csvContent = [headers, ...rows]
  .map((row) => row.map(escapeCell).join(","))
      .join("\r\n");

    // Add BOM for Excel compatibility with special characters
  const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExportCurrentPage = () => {
    if (paginatedEmployees.length === 0) return;
    exportToCsv(paginatedEmployees, `employees-page-${pagination.page}`);
    showNotification(`Exported ${paginatedEmployees.length} employees from current page`);
  };

  const handleExportAll = () => {
    // Export selected employees if any, otherwise export all filtered
    const employeesToExport =
      selectedEmployees.length > 0
        ? filteredEmployees.filter((e) => selectedEmployees.includes(e.id))
        : filteredEmployees;

    if (employeesToExport.length === 0) return;

    const filename = selectedEmployees.length > 0
      ? `selected-employees-${selectedEmployees.length}`
      : `employees-all-${employeesToExport.length}`;
    
    exportToCsv(employeesToExport, filename);
    showNotification(`Exported ${employeesToExport.length} employees`);
  };

  const handleUpdateEmployee = async (updatedEmployee) => {
    try {
      await updateEmployee(updatedEmployee.id, {
        firstName:
          updatedEmployee.name?.split(" ")[0] || updatedEmployee.firstName,
   lastName:
          updatedEmployee.name?.split(" ").slice(1).join(" ") ||
          updatedEmployee.lastName,
        email: updatedEmployee.email,
        phoneNumber: updatedEmployee.phone,
jobTitle: updatedEmployee.role,
        department: updatedEmployee.department,
     locationId: apiEmployees.find((e) => e.id === updatedEmployee.id)
  ?.locationId,
        primaryJobRoleId: apiEmployees.find((e) => e.id === updatedEmployee.id)
  ?.primaryJobRoleId,
    contractedHoursPerWeek: updatedEmployee.serviceHours,
isEligibleForTronc: updatedEmployee.isEligibleForTronc,
        isActive: updatedEmployee.isActive,
        startDate: updatedEmployee.startDate,
    updateReason: "Updated from employee detail panel",
      });
      refetch();
      setSelectedEmployee(updatedEmployee);
      showNotification("Employee updated successfully");
    } catch (err) {
      showNotification(err.message || "Update failed", "error");
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedEmployees.length === 0) return;

    try {
      switch (action) {
        case "make-eligible":
          const eligibleResult = await bulkUpdateEligibility(
        selectedEmployees,
        true,
          );
          if (eligibleResult.success && eligibleResult.data) {
  const { successCount, failedCount } = eligibleResult.data;
     if (failedCount > 0) {
       showNotification(
           `${successCount} employees marked as eligible, ${failedCount} failed`,
             "warning",
              );
         } else {
         showNotification(`${successCount} employees marked as eligible`);
     }
  } else {
  showNotification(
           "Bulk update completed with some failures",
      "warning",
            );
          }
        break;

        case "make-ineligible":
   const ineligibleResult = await bulkUpdateEligibility(
            selectedEmployees,
        false,
       );
        if (ineligibleResult.success && ineligibleResult.data) {
            const { successCount, failedCount } = ineligibleResult.data;
      if (failedCount > 0) {
              showNotification(
     `${successCount} employees marked as ineligible, ${failedCount} failed`,
      "warning",
       );
            } else {
           showNotification(
      `${successCount} employees marked as ineligible`,
  );
            }
          } else {
      showNotification(
          "Bulk update completed with some failures",
              "warning",
            );
          }
break;

  case "export":
    handleExportAll();
       return;

        case "deactivate":
          showNotification("Bulk deactivate not yet implemented", "warning");
          return;

      default:
     console.log("Unknown bulk action:", action);
          return;
      }

      await refetch();
      setSelectedEmployees([]);
    } catch (err) {
      showNotification(err.message || "Bulk action failed", "error");
    }
  };

  // Select Tenant Empty State for Global Admins
  if (isGlobalAdmin && needsTenantSelection) {
    return (
      <>
        <Helmet>
          <title>Employees - Rocket Tronc</title>
   </Helmet>
        <PageLayout
      title="Employee Management"
          description="Manage staff records, role assignments, and TRONC eligibility tracking."
          breadcrumbs={breadcrumbs}
 >
          <div className="card p-12 text-center">
 <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
      <Icon name="Building2" size={40} className="text-primary" />
    </div>
       <h2 className="text-2xl font-bold mb-3">Select a Tenant</h2>
    <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              As a Global Administrator, please select a tenant from the sidebar
              to view and manage their employees.
  </p>
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Icon name="ArrowLeft" size={16} />
   <span>Use the tenant selector in the sidebar</span>
    </div>
    </div>
        </PageLayout>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Employees - {selectedTenant?.name || "Rocket Tronc"}</title>
      </Helmet>

      <PageLayout
   title="Employee Management"
        description={
          selectedTenant
        ? `Viewing ${selectedTenant.name} - Manage staff records and TRONC eligibility.`
        : "Manage staff records, role assignments, and TRONC eligibility tracking."
   }
        breadcrumbs={breadcrumbs}
      >
    {/* Location Selector Modal for Global Admins */}
        <LocationSelectorModal />

        {/* Notification Toast */}
        {notification && (
          <div
  className={`fixed top-20 right-4 z-[1300] alert ${
           notification.type === "error"
            ? "alert-error"
        : notification.type === "warning"
      ? "alert-warning"
 : "alert-success"
    } shadow-lg animate-in slide-in-from-right`}
          >
            <Icon
          name={
       notification.type === "error"
        ? "AlertCircle"
          : notification.type === "warning"
               ? "AlertTriangle"
   : "CheckCircle"
        }
              size={20}
            />
            <span>{notification.message}</span>
            <button onClick={() => setNotification(null)} className="ml-2">
   <Icon name="X" size={16} />
   </button>
          </div>
        )}

        {/* If needsLocationSelection, block content */}
        {needsLocationSelection ? (
 <div className="card p-8 text-center mt-12">
 <Icon
     name="MapPin"
              size={32}
              className="mx-auto mb-4 text-primary"
            />
        <h3 className="text-lg font-semibold mb-2">Select a Location</h3>
            <p className="text-muted-foreground">
  Please select a location to view employee information.
  </p>
       </div>
) : (
          <>
 {/* Stats Summary */}
  <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="card p-4">
            <p className="text-sm font-medium text-muted-foreground mb-1">
            Total Employees
    </p>
       <p className="text-2xl font-bold font-data text-foreground">
        {stats.total}
            </p>
      </div>
         <div className="card p-4">
           <p className="text-sm font-medium text-muted-foreground mb-1">
       TRONC Eligible
        </p>
        <p className="text-2xl font-bold font-data text-success">
     {stats.eligible}
        </p>
     </div>
   <div className="card p-4">
       <p className="text-sm font-medium text-muted-foreground mb-1">
  Not Eligible
      </p>
     <p className="text-2xl font-bold font-data text-warning">
 {stats.notEligible}
   </p>
            </div>
            </div>

       {/* Action Buttons */}
       <div className="flex flex-wrap items-center gap-3 mb-6">
              <button
         className="btn btn-primary"
      onClick={() => setIsAddModalOpen(true)}
  >
<Icon name="UserPlus" size={18} />
          Add Employee
     </button>
   <button
   className="btn btn-secondary"
     onClick={() => setIsImportModalOpen(true)}
          >
        <Icon name="Upload" size={18} />
    Import CSV
    </button>
              <button
          className="btn btn-outline"
       onClick={handleSyncEmployees}
              disabled={mutationLoading}
  >
         <Icon
            name="RefreshCw"
 size={18}
            className={mutationLoading ? "animate-spin" : ""}
                />
     Sync from HR System
   </button>
            </div>

            {/* Loading State */}
  {loading && (
              <div className="card p-8 text-center mb-6">
                <div className="spinner mx-auto mb-4" />
        <p className="text-muted-foreground">Loading employees...</p>
   </div>
          )}

       {/* Error State */}
   {error && !loading && (
   <div className="alert alert-error mb-6">
         <Icon name="AlertCircle" size={20} />
           <div>
          <p className="alert-title">Unable to load employees</p>
       <p className="alert-description">
           {error}. Please check that the API server is running and try
                    again.
      </p>
       </div>
            <button
 className="btn btn-outline btn-sm ml-auto"
        onClick={refetch}
    >
           <Icon name="RefreshCw" size={16} />
    Retry
    </button>
   </div>
   )}

          {/* Content */}
   {!loading && !error && (
              <>
    {transformedEmployees.length === 0 ? (
         <div className="card p-12 text-center">
      <div className="empty-state-icon mx-auto mb-4">
          <Icon name="Users" size={32} />
        </div>
      <h3 className="text-lg font-semibold mb-2">
          No employees yet
      </h3>
<p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Get started by adding employees manually, importing from
          CSV, or syncing from your HR system.
        </p>
                    <div className="flex gap-3 justify-center">
       <button
    className="btn btn-primary"
           onClick={() => setIsAddModalOpen(true)}
          >
    <Icon name="UserPlus" size={18} />
         Add Employee
            </button>
      <button
     className="btn btn-secondary"
     onClick={() => setIsImportModalOpen(true)}
             >
              <Icon name="Upload" size={18} />
            Import CSV
    </button>
          <button
       className="btn btn-outline"
        onClick={handleSyncEmployees}
       >
       <Icon name="RefreshCw" size={18} />
        Sync from HR
      </button>
       </div>
      </div>
     ) : (
           <>
         {/* Filters */}
              <PageSection>
       <EmployeeFilters
   searchTerm={searchTerm}
        onSearchChange={handleFilterChange(setSearchTerm)}
             selectedRole={selectedRole}
     onRoleChange={handleFilterChange(setSelectedRole)}
              selectedDepartment={selectedDepartment}
         onDepartmentChange={handleFilterChange(setSelectedDepartment)}
   selectedEmploymentType={selectedEmploymentType}
     onEmploymentTypeChange={handleFilterChange(setSelectedEmploymentType)}
      selectedEligibilityStatus={selectedEligibilityStatus}
    onEligibilityStatusChange={handleFilterChange(setSelectedEligibilityStatus)}
            jobRoles={jobRoles || []}
 />
     </PageSection>

  {/* Bulk Actions */}
                {selectedEmployees?.length > 0 && (
        <BulkActionsBar
       selectedCount={selectedEmployees?.length}
           onBulkAction={handleBulkAction}
      onClearSelection={() => setSelectedEmployees([])}
       />
     )}

  {/* Employee Table */}
         <PageSection>
    {filteredEmployees.length === 0 ? (
     <div className="card p-8 text-center">
        <Icon
           name="Search"
        size={32}
  className="mx-auto mb-4 text-muted-foreground"
        />
            <p className="text-muted-foreground">
      No employees match your filters
   </p>
           </div>
      ) : (
             <>
       <EmployeeListTable
             employees={paginatedEmployees}
       onEmployeeClick={handleEmployeeClick}
       selectedEmployees={selectedEmployees}
     onSelectionChange={setSelectedEmployees}
  />
     
        {/* Pagination with Export */}
      <Pagination
     page={pagination.page}
         pageSize={pagination.pageSize}
            totalCount={filteredEmployees.length}
      totalPages={totalPages}
       onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
          onExportCurrentPage={handleExportCurrentPage}
  onExportAll={handleExportAll}
exportDisabled={filteredEmployees.length === 0}
       className="mt-4"
          />
    </>
   )}
      </PageSection>
           </>
      )}
     </>
    )}
          </>
        )}
      </PageLayout>

      {/* Detail Panel */}
      <EmployeeDetailPanel
        employee={selectedEmployee}
        isOpen={isDetailPanelOpen}
        onClose={handleCloseDetailPanel}
        onUpdate={handleUpdateEmployee}
      />

      {/* Add Employee Modal */}
      <AddEmployeeModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
    onSubmit={handleAddEmployee}
        jobRoles={jobRoles || []}
        locations={locations || []}
     loading={mutationLoading}
      />

      {/* CSV Import Modal */}
      <CsvImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
   onImport={handleImportEmployees}
        loading={mutationLoading}
/>
    </>
  );
};

export default Employees;
